// src/services/holidayService.js

/**
 * Holiday Service - Manages company holidays in Firestore
 * Handles CRUD operations for holidays with support for recurring holidays
 * and Philippine holiday presets
 */

import {
  collection,
  doc,
  setDoc,
  getDoc,
  getDocs,
  updateDoc,
  deleteDoc,
  query,
  where,
  orderBy,
  serverTimestamp,
} from "firebase/firestore";
import { db } from "../firebase/config";

class HolidayService {
  /**
   * Philippine Holiday Presets
   * These are the official regular and special holidays in the Philippines
   */
  static PHILIPPINE_HOLIDAYS = {
    regular: [
      {
        name: "New Year's Day",
        type: "regular",
        isRecurring: true,
        recurringRule: { frequency: "yearly", month: 1, day: 1 },
      },
      {
        name: "Araw ng Kagitingan (Day of Valor)",
        type: "regular",
        isRecurring: true,
        recurringRule: { frequency: "yearly", month: 4, day: 9 },
      },
      {
        name: "Labor Day",
        type: "regular",
        isRecurring: true,
        recurringRule: { frequency: "yearly", month: 5, day: 1 },
      },
      {
        name: "Independence Day",
        type: "regular",
        isRecurring: true,
        recurringRule: { frequency: "yearly", month: 6, day: 12 },
      },
      {
        name: "National Heroes Day",
        type: "regular",
        isRecurring: true,
        recurringRule: { frequency: "yearly", month: 8, dayOfWeek: 1, weekNumber: -1 }, // Last Monday of August
      },
      {
        name: "Bonifacio Day",
        type: "regular",
        isRecurring: true,
        recurringRule: { frequency: "yearly", month: 11, day: 30 },
      },
      {
        name: "Christmas Day",
        type: "regular",
        isRecurring: true,
        recurringRule: { frequency: "yearly", month: 12, day: 25 },
      },
      {
        name: "Rizal Day",
        type: "regular",
        isRecurring: true,
        recurringRule: { frequency: "yearly", month: 12, day: 30 },
      },
    ],
    special: [
      {
        name: "Chinese New Year",
        type: "special",
        isRecurring: false, // Date varies each year
      },
      {
        name: "Black Saturday",
        type: "special",
        isRecurring: false, // Moveable feast
      },
      {
        name: "Ninoy Aquino Day",
        type: "special",
        isRecurring: true,
        recurringRule: { frequency: "yearly", month: 8, day: 21 },
      },
      {
        name: "All Saints' Day",
        type: "special",
        isRecurring: true,
        recurringRule: { frequency: "yearly", month: 11, day: 1 },
      },
      {
        name: "Immaculate Conception Day",
        type: "special",
        isRecurring: true,
        recurringRule: { frequency: "yearly", month: 12, day: 8 },
      },
      {
        name: "Last Day of the Year",
        type: "special",
        isRecurring: true,
        recurringRule: { frequency: "yearly", month: 12, day: 31 },
      },
    ],
    moveable: [
      {
        name: "Maundy Thursday",
        type: "regular",
        isRecurring: false, // Calculated based on Easter
      },
      {
        name: "Good Friday",
        type: "regular",
        isRecurring: false, // Calculated based on Easter
      },
      {
        name: "Eid'l Fitr",
        type: "regular",
        isRecurring: false, // Based on Islamic calendar
      },
      {
        name: "Eid'l Adha",
        type: "regular",
        isRecurring: false, // Based on Islamic calendar
      },
    ],
  };

  /**
   * Get holidays for a specific year
   * @param {string} companyId - Company ID
   * @param {number} year - Year to fetch holidays for
   * @returns {Promise<Array>} Array of holidays
   */
  async getHolidaysByYear(companyId, year) {
    try {
      const holidaysRef = collection(db, "companies", companyId, "holidays", String(year), "items");
      const q = query(holidaysRef, orderBy("date", "asc"));
      const snapshot = await getDocs(q);

      return snapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));
    } catch (error) {
      console.error("Error fetching holidays:", error);
      throw error;
    }
  }

  /**
   * Get holiday by specific date
   * @param {string} companyId - Company ID
   * @param {Date} date - Date to check
   * @returns {Promise<Object|null>} Holiday object or null
   */
  async getHolidayByDate(companyId, date) {
    try {
      const year = date.getFullYear();
      const dateString = date.toISOString().split("T")[0]; // YYYY-MM-DD format
      
      const holidaysRef = collection(db, "companies", companyId, "holidays", String(year), "items");
      const q = query(holidaysRef, where("date", "==", dateString));
      const snapshot = await getDocs(q);

      if (snapshot.empty) {
        return null;
      }

      return {
        id: snapshot.docs[0].id,
        ...snapshot.docs[0].data(),
      };
    } catch (error) {
      console.error("Error fetching holiday by date:", error);
      throw error;
    }
  }

  /**
   * Create a new holiday
   * @param {string} companyId - Company ID
   * @param {Object} holidayData - Holiday data
   * @returns {Promise<string>} Created holiday ID
   */
  async createHoliday(companyId, holidayData) {
    try {
      const year = new Date(holidayData.date).getFullYear();
      const holidayId = `${holidayData.date}-${Date.now()}`;
      const holidayRef = doc(
        db,
        "companies",
        companyId,
        "holidays",
        String(year),
        "items",
        holidayId
      );

      await setDoc(holidayRef, {
        ...holidayData,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      });

      return holidayId;
    } catch (error) {
      console.error("Error creating holiday:", error);
      throw error;
    }
  }

  /**
   * Update an existing holiday
   * @param {string} companyId - Company ID
   * @param {string} year - Year of the holiday
   * @param {string} holidayId - Holiday ID
   * @param {Object} updates - Updates to apply
   * @returns {Promise<void>}
   */
  async updateHoliday(companyId, year, holidayId, updates) {
    try {
      const holidayRef = doc(
        db,
        "companies",
        companyId,
        "holidays",
        String(year),
        "items",
        holidayId
      );

      await updateDoc(holidayRef, {
        ...updates,
        updatedAt: serverTimestamp(),
      });
    } catch (error) {
      console.error("Error updating holiday:", error);
      throw error;
    }
  }

  /**
   * Delete a holiday
   * @param {string} companyId - Company ID
   * @param {string} year - Year of the holiday
   * @param {string} holidayId - Holiday ID
   * @returns {Promise<void>}
   */
  async deleteHoliday(companyId, year, holidayId) {
    try {
      const holidayRef = doc(
        db,
        "companies",
        companyId,
        "holidays",
        String(year),
        "items",
        holidayId
      );

      await deleteDoc(holidayRef);
    } catch (error) {
      console.error("Error deleting holiday:", error);
      throw error;
    }
  }

  /**
   * Apply Philippine holiday presets for a specific year
   * @param {string} companyId - Company ID
   * @param {number} year - Year to apply presets to
   * @param {Array} selectedPresets - Array of preset holiday names to apply
   * @returns {Promise<void>}
   */
  async applyPhilippinePresets(companyId, year, selectedPresets) {
    try {
      const allPresets = [
        ...HolidayService.PHILIPPINE_HOLIDAYS.regular,
        ...HolidayService.PHILIPPINE_HOLIDAYS.special,
      ];

      const promises = selectedPresets.map(async (presetName) => {
        const preset = allPresets.find((h) => h.name === presetName);
        if (!preset) return;

        // Calculate date for the specific year
        let date;
        if (preset.isRecurring && preset.recurringRule) {
          date = this.calculateRecurringDate(year, preset.recurringRule);
        } else {
          // For non-recurring holidays, admin must set the date manually
          return;
        }

        if (date) {
          const holidayData = {
            name: preset.name,
            type: preset.type,
            date: date.toISOString().split("T")[0],
            isRecurring: preset.isRecurring,
            recurringRule: preset.recurringRule,
            isPreset: true,
          };

          await this.createHoliday(companyId, holidayData);
        }
      });

      await Promise.all(promises);
    } catch (error) {
      console.error("Error applying Philippine presets:", error);
      throw error;
    }
  }

  /**
   * Calculate date for recurring holidays
   * @param {number} year - Year to calculate for
   * @param {Object} rule - Recurring rule object
   * @returns {Date|null} Calculated date or null
   */
  calculateRecurringDate(year, rule) {
    if (!rule || rule.frequency !== "yearly") return null;

    // Fixed date holidays
    if (rule.month && rule.day) {
      return new Date(year, rule.month - 1, rule.day);
    }

    // Holidays based on day of week (e.g., last Monday of August)
    if (rule.month && rule.dayOfWeek !== undefined && rule.weekNumber) {
      const firstDay = new Date(year, rule.month - 1, 1);
      const lastDay = new Date(year, rule.month, 0);

      if (rule.weekNumber === -1) {
        // Last occurrence of the day in the month
        for (let day = lastDay.getDate(); day >= 1; day--) {
          const date = new Date(year, rule.month - 1, day);
          if (date.getDay() === rule.dayOfWeek) {
            return date;
          }
        }
      }
    }

    return null;
  }

  /**
   * Get all Philippine holiday presets (excluding moveable)
   * @returns {Array} Array of all preset holidays
   */
  getPhilippinePresets() {
    return [
      ...HolidayService.PHILIPPINE_HOLIDAYS.regular,
      ...HolidayService.PHILIPPINE_HOLIDAYS.special,
    ];
  }

  /**
   * Get moveable holidays that need manual date setting
   * @returns {Array} Array of moveable holiday names
   */
  getMoveableHolidays() {
    return HolidayService.PHILIPPINE_HOLIDAYS.moveable.map(h => h.name);
  }

  /**
   * Check if a date is a holiday
   * @param {string} companyId - Company ID
   * @param {Date} date - Date to check
   * @returns {Promise<Object|null>} Holiday info or null
   */
  async isHoliday(companyId, date) {
    return this.getHolidayByDate(companyId, date);
  }

  /**
   * Get all holidays within a date range
   * Handles cross-year ranges by fetching from multiple years if needed
   * @param {string} companyId - Company ID
   * @param {Date} startDate - Start date of the range
   * @param {Date} endDate - End date of the range
   * @returns {Promise<Array>} Array of holidays within the range
   */
  async getHolidaysInRange(companyId, startDate, endDate) {
    try {
      const startYear = startDate.getFullYear();
      const endYear = endDate.getFullYear();
      const startDateString = startDate.toISOString().split("T")[0];
      const endDateString = endDate.toISOString().split("T")[0];

      const allHolidays = [];

      // Fetch holidays from each year in the range
      for (let year = startYear; year <= endYear; year++) {
        const yearHolidays = await this.getHolidaysByYear(companyId, year);

        // Filter to only include holidays within the date range
        const filteredHolidays = yearHolidays.filter((holiday) => {
          const holidayDate = holiday.date;
          return holidayDate >= startDateString && holidayDate <= endDateString;
        });

        allHolidays.push(...filteredHolidays);
      }

      // Sort by date
      allHolidays.sort((a, b) => a.date.localeCompare(b.date));

      return allHolidays;
    } catch (error) {
      console.error("Error fetching holidays in range:", error);
      throw error;
    }
  }

  /**
   * Get all holidays for a specific month (useful for payroll processing)
   * @param {string} companyId - Company ID
   * @param {number} year - Year
   * @param {number} month - Month (1-12)
   * @returns {Promise<Array>} Array of holidays in the month
   */
  async getHolidaysByMonth(companyId, year, month) {
    try {
      // Create start and end dates for the month
      const startDate = new Date(year, month - 1, 1);
      const endDate = new Date(year, month, 0); // Last day of the month

      return this.getHolidaysInRange(companyId, startDate, endDate);
    } catch (error) {
      console.error("Error fetching holidays by month:", error);
      throw error;
    }
  }

  /**
   * Get all holidays for a pay period (supports cross-month periods)
   * @param {string} companyId - Company ID
   * @param {Date} periodStart - Pay period start date
   * @param {Date} periodEnd - Pay period end date
   * @returns {Promise<Array>} Array of holidays in the pay period
   */
  async getHolidaysForPayPeriod(companyId, periodStart, periodEnd) {
    return this.getHolidaysInRange(companyId, periodStart, periodEnd);
  }
}

// Export singleton instance
export const holidayService = new HolidayService();